import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Star, Shield, CheckCircle, ChevronDown, ChevronUp, Calendar, ArrowRight, Award, Loader2, Stethoscope, Zap, Users, Heart } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import LandingPageHeader from '../components/LandingPageHeader';
import { SEOHead, BreadcrumbSchema, FAQSchema, MedicalSpecialtySchema } from '../components/SEOHead';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/t64300ho_SH%20Logo.png";

const UrologyPage = () => {
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', treatment: '' });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.email || !formData.treatment) {
      toast.error('Please fill all fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/specialty-leads`, { ...formData, specialty: 'Urology', source_page: '/urology' });
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'form_submission', form_name: 'urology_lead', form_department: 'Urology', form_source: '/urology' });
      }
      if (window.gtag) { window.gtag('event', 'conversion', { send_to: 'AW-17542370694/urology_lead' }); }
      toast.success('Thank you! Dr. Venkat\'s team will contact you shortly.');
      setFormData({ name: '', phone: '', email: '', treatment: '' });
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const treatments = [
    'Kidney Stones', 'Prostate Issues (BPH)', 'Urinary Tract Infection', 'Urological Cancer',
    'Male Fertility', 'Kidney Transplant', 'Robotic Surgery Consultation', 'Other',
  ];

  const conditions = [
    { title: 'Laser Stone Removal', desc: 'Advanced laser lithotripsy for pain-free, precise kidney and ureteric stone removal', icon: Zap },
    { title: 'Laser Prostate Surgery', desc: 'Modern laser techniques for enlarged prostate — minimal bleeding, faster recovery', icon: Shield },
    { title: 'Mini PCNL', desc: 'Miniature Percutaneous Nephrolithotomy for complex kidney stones with tiny incisions', icon: Zap },
    { title: 'Kidney Transplantation', desc: 'Comprehensive transplant services — donor evaluation, surgery & post-transplant care', icon: Award },
    { title: 'Robotic Surgery', desc: 'Da Vinci certified robotic procedures for precision, minimal scarring & fast recovery', icon: Shield },
    { title: 'Paediatric Urology', desc: 'Specialized treatment for urological conditions and congenital abnormalities in children', icon: Stethoscope },
    { title: 'Reconstructive Urology', desc: 'Urethral stricture, ureteric reimplantation & pelvic reconstructive procedures', icon: CheckCircle },
    { title: 'Female Urinary Incontinence', desc: 'Advanced treatments to restore urinary control and improve quality of life', icon: Users },
    { title: 'Urological Cancers', desc: 'Comprehensive cancer management — prostate, bladder, kidney & testicular cancers', icon: Shield },
    { title: 'Andrology', desc: 'Male sexual health, fertility evaluation & treatment including micro-surgical procedures', icon: Heart },
    { title: 'Benign Prostatic Hyperplasia', desc: 'Multiple treatment options for enlarged prostate from medication to laser surgery', icon: Heart },
    { title: 'Hydronephrosis & PUJ Obstruction', desc: 'Laparoscopic/robotic pyeloplasty for kidney drainage issues and PUJ obstruction', icon: Zap },
  ];

  const doctorBadges = [
    { icon: Award, text: 'Robotic Surgery Certified', sub: 'Intuitive Surgicals, USA' },
    { icon: Stethoscope, text: 'Patient-Centered Care', sub: '100% Patient Focused' },
    { icon: Zap, text: 'Minimally Invasive Expert', sub: 'Faster Recovery' },
    { icon: Users, text: '3 Languages', sub: 'English, Tamil, Hindi' },
  ];

  const education = [
    { degree: 'M.S (General Surgery)', institution: 'Post Graduate in Surgery' },
    { degree: 'M.Ch (Urology)', institution: 'Super Specialization in Urology' },
    { degree: 'D.Lap', institution: 'Aesculap Academy, Germany' },
    { degree: 'Robotic Surgery Certified', institution: 'Intuitive Surgicals, USA' },
  ];

  const memberships = [
    'Urological Society of India',
    'Association of Southern Urologists',
    'Tamilnadu and Pondicherry Association of Urologists',
    'Association of Surgeons of India',
  ];

  const testimonials = [
    { name: 'Rajesh M.', location: 'Triplicane', condition: 'Kidney Stone Treatment', rating: 5, text: 'Had severe kidney stone pain. Dr. Venkat performed laser surgery and I was discharged the next day. Excellent experience with minimal pain.' },
    { name: 'Suresh K.', location: 'T. Nagar', condition: 'BPH Treatment', rating: 5, text: 'After years of prostate problems, Dr. Venkat\'s minimally invasive procedure solved my issue completely. The staff was very supportive.' },
    { name: 'Mohammed A.', location: 'Royapettah', condition: 'Robotic Surgery', rating: 5, text: 'Dr. Venkat is a true expert in robotic surgery. My recovery was incredibly fast with minimal scarring. Highly recommended.' },
  ];

  const faqs = [
    { q: 'What conditions does Dr. Venkatsubramaniam treat?', a: 'Dr. Venkat treats a wide range of urological conditions including kidney stones, prostate diseases, urinary tract infections, urological cancers, male fertility issues, and performs kidney transplants and robotic surgeries.' },
    { q: 'What is the advantage of robotic surgery?', a: 'Robotic surgery offers greater precision, smaller incisions, less blood loss, reduced pain, shorter hospital stays, and faster recovery compared to traditional open surgery.' },
    { q: 'How long is the recovery after kidney stone laser treatment?', a: 'Most patients can go home within 24-48 hours after laser lithotripsy. Full recovery typically takes 1-2 weeks, with most patients returning to normal activities within a few days.' },
    { q: 'Does Sakthi Hospital handle kidney transplants?', a: 'Yes, Dr. Venkatsubramaniam is an experienced transplant surgeon. Our hospital provides comprehensive transplant services including donor evaluation, surgery, and post-transplant care.' },
    { q: 'How can I book a consultation?', a: 'You can fill the appointment form on this page, call +91 98402 64444, or WhatsApp us. Our team will schedule your consultation with Dr. Venkat at a convenient time.' },
  ];

  const LeadForm = ({ className = '' }) => (
    <form onSubmit={handleSubmit} className={className}>
      <input type="hidden" id="zc_gad" name="zc_gad" value="" />
      <div className="space-y-3">
        <div>
          <Label className="text-sm font-medium text-gray-700">Full Name</Label>
          <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Your Full Name" className="mt-1 h-11" data-testid="uro-form-name" />
        </div>
        <div>
          <Label className="text-sm font-medium text-gray-700">Contact Number</Label>
          <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+91 98402 64444" className="mt-1 h-11" data-testid="uro-form-phone" />
        </div>
        <div>
          <Label className="text-sm font-medium text-gray-700">Email Address</Label>
          <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="your@email.com" className="mt-1 h-11" data-testid="uro-form-email" />
        </div>
        <div>
          <Label className="text-sm font-medium text-gray-700">Reason for Consultation</Label>
          <Select value={formData.treatment} onValueChange={(v) => setFormData({...formData, treatment: v})}>
            <SelectTrigger className="mt-1 h-11" data-testid="uro-form-treatment"><SelectValue placeholder="Select consultation type" /></SelectTrigger>
            <SelectContent>{treatments.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={loading} className="w-full h-12 bg-[#0097a7] hover:bg-[#00838f] text-white font-semibold rounded-lg" data-testid="uro-form-submit">
          {loading ? <><Loader2 size={18} className="animate-spin mr-2" /> Submitting...</> : <><Calendar size={18} className="mr-2" /> Submit Appointment Request</>}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="min-h-screen bg-white" data-testid="urology-page">
      <SEOHead
        title="Best Urologist in Chennai - Kidney Stone & Prostate Treatment | Sakthi Hospital"
        description="Dr. Venkatsubramaniam - Senior Urologist at Sakthi Hospital Chennai. Expert in laser kidney stone removal, prostate treatment, robotic surgery, kidney transplant. 12+ advanced procedures. NABH Accredited. Book free consultation."
        path="/urology"
        keywords="urologist Chennai, kidney stone treatment Chennai, prostate treatment Triplicane, laser lithotripsy, kidney transplant Chennai, robotic urology surgery, best urologist near me, Dr Venkatsubramaniam, urology hospital Chennai"
      />
      <BreadcrumbSchema items={[{ name: 'Home', path: '/' }, { name: 'Urology', path: '/urology' }]} />
      <FAQSchema questions={faqs} />
      <MedicalSpecialtySchema
        name="Urology"
        description="Advanced urological care at Sakthi Hospital Chennai. Laser kidney stone removal, prostate treatment, robotic surgery, kidney transplant by Dr. Venkatsubramaniam."
        procedures={['Laser Kidney Stone Removal', 'Mini PCNL', 'Prostate Treatment', 'Robotic Surgery', 'Kidney Transplant', 'Paediatric Urology', 'Andrology', 'Urological Cancer Surgery']}
        doctor={{ name: 'Dr. D. Venkatsubramaniam', qualifications: 'Senior Urologist & Robotic Surgeon' }}
      />
      {/* Header */}
      <LandingPageHeader accentColor="#0097a7" subtitle="Urology Department" />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#e0f7fa] via-white to-[#e0f2f1] overflow-hidden" data-testid="uro-hero">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#0097a7]/5 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 py-12 lg:py-16">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            {/* Left - Doctor Photo + Info */}
            <div className="lg:col-span-7 flex flex-col md:flex-row gap-8 items-center md:items-start">
              {/* Doctor Photo */}
              <div className="flex-shrink-0" data-testid="dr-venkat-photo">
                <div className="relative">
                  <div className="w-52 h-64 md:w-60 md:h-72 rounded-2xl overflow-hidden shadow-xl border-4 border-white ring-2 ring-[#0097a7]/20">
                    <img
                      src="https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/jy8pz5v3_Venkat%20Photo.png"
                      alt="Dr. Venkatsubramaniam - Senior Urologist"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-[#0097a7] text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-lg whitespace-nowrap">
                    15+ Years Excellence
                  </div>
                </div>
              </div>

              {/* Doctor Info */}
              <div className="space-y-4 text-center md:text-left">
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  <span className="px-3 py-1 bg-[#0097a7]/10 text-[#0097a7] rounded-full text-xs font-semibold flex items-center gap-1"><CheckCircle size={12} /> Robotic Surgery Certified</span>
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0f172a] leading-[1.1] tracking-tight" data-testid="uro-headline">
                  Dr. Venkat&shy;subramaniam
                  <span className="block text-base sm:text-lg font-semibold text-[#0097a7] mt-2">M.S, D.Lap, M.Ch (Uro)</span>
                  <span className="block text-sm font-medium text-[#64748b] mt-1">Senior Urologist, Laparoscopic, Transplant & Robotic Surgeon</span>
                </h1>
                <p className="text-lg font-semibold text-[#0f172a] italic">"Technology Led - Expertise Driven"</p>

                {/* Stats Row */}
                <div className="flex flex-wrap gap-6 justify-center md:justify-start">
                  <div className="text-center"><div className="text-2xl font-extrabold text-[#0097a7]">15+</div><div className="text-xs text-[#64748b]">Years Excellence</div></div>
                  <div className="text-center"><div className="text-2xl font-extrabold text-[#d4a24c] flex items-center gap-1">4.9+ <Star size={16} fill="#d4a24c" stroke="#d4a24c" /></div><div className="text-xs text-[#64748b]">Rating</div></div>
                  <div className="text-center"><div className="text-2xl font-extrabold text-[#0097a7]">10,000+</div><div className="text-xs text-[#64748b]">Patients</div></div>
                </div>

                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                  <a href="tel:+919840264444" className="bg-[#0097a7] text-white px-6 py-3 rounded-lg font-semibold shadow-lg shadow-[#0097a7]/25 hover:bg-[#00838f] transition-all flex items-center gap-2" data-testid="uro-call-btn">
                    <Phone size={18} /> Book Appointment
                  </a>
                  <a href="#uro-booking" className="bg-white text-[#0097a7] border-2 border-[#0097a7] px-6 py-3 rounded-lg font-semibold hover:bg-[#0097a7] hover:text-white transition-all flex items-center gap-2">
                    <Calendar size={18} /> Schedule Consultation
                  </a>
                </div>
              </div>
            </div>

            {/* Right - Lead Form */}
            <div className="lg:col-span-5">
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-teal-100">
                <div className="text-center mb-4">
                  <h3 className="text-lg font-bold text-[#0f172a]">Schedule Your Consultation</h3>
                  <p className="text-sm text-[#64748b]">Dr. Venkat's team will contact you shortly</p>
                </div>
                <LeadForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conditions Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] mb-3">Comprehensive Urological Services</h2>
            <p className="text-[#64748b] max-w-xl mx-auto">Expert treatment for all urological conditions with advanced technology</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {conditions.map((c) => (
              <div key={c.title} className="bg-gradient-to-br from-[#e0f7fa] to-white rounded-xl p-5 border border-teal-50 hover:shadow-md transition-all" data-testid={`uro-condition-${c.title}`}>
                <c.icon size={24} className="text-[#0097a7] mb-3" />
                <h3 className="text-sm font-bold text-[#0f172a] mb-1">{c.title}</h3>
                <p className="text-xs text-[#5a6b7c] leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Doctor */}
      <section className="py-16 bg-gradient-to-b from-[#e0f7fa] to-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-[#0f172a] mb-10">Meet Dr. Venkatsubramaniam</h2>
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-teal-100">
            <p className="text-[#3a4a5c] leading-relaxed mb-6">
              Dr. Venkatsubramaniam is a Senior Urologist with over 15 years of surgical excellence at Sakthi Hospital, Chennai. Specializing in advanced robotic surgery, comprehensive urological care, and kidney transplantation, he combines cutting-edge technology with a patient-first approach. His meticulous technique, clear communication, and dedication to patient welfare have earned the trust of over 10,000 patients.
            </p>
            <p className="text-[#3a4a5c] leading-relaxed mb-8">
              Certified in Robotic Surgery by Intuitive Surgicals (USA) and trained in laparoscopy at Aesculap Academy (Germany), Dr. Venkat brings world-class surgical expertise to every patient. He communicates fluently in English, Tamil, and Hindi, ensuring every patient feels understood and cared for.
            </p>

            {/* Badges */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {doctorBadges.map((b) => (
                <div key={b.text} className="flex items-center gap-3 bg-[#e0f7fa] rounded-xl p-3">
                  <b.icon size={24} className="text-[#0097a7] flex-shrink-0" />
                  <div><div className="text-sm font-semibold text-[#0f172a]">{b.text}</div><div className="text-xs text-[#94a3b8]">{b.sub}</div></div>
                </div>
              ))}
            </div>

            {/* Education & Memberships side by side */}
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-bold text-[#0f172a] mb-4">Education & Certifications</h3>
                <div className="space-y-3">
                  {education.map((e) => (
                    <div key={e.degree} className="flex items-start gap-3">
                      <CheckCircle size={18} className="text-[#0097a7] mt-0.5 flex-shrink-0" />
                      <div><div className="font-semibold text-sm text-[#0f172a]">{e.degree}</div><div className="text-xs text-[#94a3b8]">{e.institution}</div></div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#0f172a] mb-4">Professional Memberships</h3>
                <div className="space-y-3">
                  {memberships.map((m) => (
                    <div key={m} className="flex items-center gap-3">
                      <Award size={18} className="text-[#0097a7] flex-shrink-0" />
                      <span className="text-sm text-[#3a4a5c]">{m}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-[#0f172a] mb-10">What Our Patients Say</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-gradient-to-br from-[#e0f7fa] to-white rounded-2xl p-6 border border-teal-50 shadow-sm" data-testid={`uro-testimonial-${t.name}`}>
                <div className="text-xs font-semibold text-[#0097a7] mb-2">{t.condition}</div>
                <div className="flex gap-0.5 mb-3">{[...Array(t.rating)].map((_, j) => <Star key={`star-${j}`} size={16} fill="#d4a24c" stroke="#d4a24c" />)}</div>
                <p className="text-sm text-[#3a4a5c] leading-relaxed mb-4 italic">"{t.text}"</p>
                <div className="flex items-center gap-3 pt-3 border-t border-teal-100">
                  <div className="w-10 h-10 bg-[#0097a7] rounded-full flex items-center justify-center text-white font-bold text-sm">{t.name[0]}</div>
                  <div>
                    <div className="font-semibold text-sm text-[#0f172a]">{t.name}</div>
                    <div className="text-xs text-[#94a3b8]">{t.location}, Chennai</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gradient-to-b from-[#e0f7fa] to-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-[#0f172a] mb-10">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={faq.q} className="border border-teal-100 rounded-xl overflow-hidden" data-testid={`uro-faq-${idx}`}>
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between p-5 text-left hover:bg-[#e0f7fa] transition-colors">
                  <span className="font-semibold text-[#0f172a] pr-4">{faq.q}</span>
                  {openFaq === idx ? <ChevronUp size={20} className="text-[#0097a7] flex-shrink-0" /> : <ChevronDown size={20} className="text-[#94a3b8] flex-shrink-0" />}
                </button>
                {openFaq === idx && <div className="px-5 pb-5 text-sm text-[#5a6b7c] leading-relaxed">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-[#0f172a] mb-8">Visit Us</h2>
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="rounded-2xl overflow-hidden h-72 shadow-lg">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.0!2d80.2757397!3d13.0570045!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a526605d0a030d5%3A0x6e079bd99529d311!2sSakthi%20Hospital!5e0!3m2!1sen!2sin!4v1704067200000!5m2!1sen!2sin" width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="Sakthi Hospital Location"></iframe>
            </div>
            <div className="flex flex-col justify-center space-y-4">
              <div className="flex items-start gap-3"><MapPin size={20} className="text-[#0097a7] mt-1 flex-shrink-0" /><div><p className="font-semibold text-[#0f172a]">Sakthi Hospital & Research Centre</p><p className="text-sm text-[#5a6b7c]">175, Big Street, Triplicane, Chennai - 600005</p></div></div>
              <div className="flex items-center gap-3"><Phone size={20} className="text-[#0097a7] flex-shrink-0" /><div><p className="text-sm text-[#5a6b7c]">+91 44 2844 2626 | +91 98402 64444</p></div></div>
              <div className="flex items-center gap-3"><Clock size={20} className="text-[#0097a7] flex-shrink-0" /><div><p className="text-sm text-[#5a6b7c]">OPD: 9:00 AM - 9:00 PM | Emergency: 24/7</p></div></div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking CTA */}
      <section id="uro-booking" className="py-16 bg-gradient-to-r from-[#00838f] to-[#0097a7]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="text-white space-y-4">
              <h2 className="text-3xl font-bold">Schedule Your Consultation with Dr. Venkat</h2>
              <p className="opacity-90">Take the first step towards expert urological care. Book your appointment today.</p>
              <div className="flex flex-wrap gap-3">
                <a href="tel:+919840264444" className="bg-white text-[#00838f] px-6 py-3 rounded-lg font-semibold flex items-center gap-2 hover:bg-gray-50 transition-colors">
                  <Phone size={18} /> Call Now
                </a>
                <a href="https://wa.me/919840264444" target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 hover:bg-[#20bd5a] transition-colors">
                  WhatsApp Now
                </a>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-xl">
              <h3 className="text-lg font-bold text-center text-[#0f172a] mb-4">Book Your Appointment</h3>
              <LeadForm />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <div className="bg-[#0f172a] text-white py-6 text-center">
        <Link to="/" className="text-sm text-white/70 hover:text-[#0097a7] transition-colors flex items-center gap-2 justify-center">
          <ArrowRight size={14} className="rotate-180" /> Back to Sakthi Hospital Home
        </Link>
        <p className="text-xs text-white/50 mt-2">© 2025 Sakthi Hospital & Research Centre. All Rights Reserved.</p>
      </div>
    </div>
  );
};

export default UrologyPage;
